import { useLayoutEffect, useRef, useState } from 'react';

/*
 * One scroll per page: a long table takes exactly the height left between its top and the bottom
 * of the screen, so its rows scroll inside it (header stays sticky) and the page itself does not
 * scroll a second time.
 *
 *   const { ref, height, active } = useViewportFit(enabled, { min, tailSelector });
 *
 * - height: the max-height in px (null until measured, or when not active).
 * - active: false inside a dialog (dialogs keep their own limit).
 * - tailSelector: an ancestor whose part BELOW the element must stay on screen too
 *   (e.g. the DataTable card with its pagination footer).
 * Re-measured on window resize and whenever the page content (#main) changes size.
 */
export default function useViewportFit(enabled, { min = 320, tailSelector, ref: externalRef } = {}) {
    const ownRef = useRef(null);
    const ref = externalRef || ownRef;
    const [height, setHeight] = useState(null);
    const [active, setActive] = useState(false);
    // the element can appear after mount (a page renders its table once data has loaded)
    const node = useAttachedNode(ref);

    useLayoutEffect(() => {
        if (!enabled || !node || node.closest('[role="dialog"]')) {
            setActive(false);
            setHeight(null);
            return undefined;
        }
        setActive(true);
        let frame = 0;
        const measure = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const el = ref.current;
                if (!el || el.offsetParent === null) return; // hidden (inactive tab): measure when shown
                const rect = el.getBoundingClientRect();
                const top = rect.top + window.scrollY;
                const main = document.getElementById('main');
                // Space the wrappers keep below the table: own bottom margin, then every wrapper's
                // bottom padding + border up to (and including) the page content area.
                const px = (v) => parseFloat(v) || 0;
                // Also the content that follows it in a stacked (block / column) layout, e.g. a
                // "Showing x of y" line under the table.
                const outer = (n) => {
                    const cs = getComputedStyle(n);
                    if (cs.position === 'fixed' || cs.position === 'absolute') return 0; // overlays take no room
                    return n.offsetHeight + px(cs.marginTop) + px(cs.marginBottom);
                };
                const stacked = (p) => { const cs = getComputedStyle(p); return !(cs.display.includes('grid') || (cs.display.includes('flex') && !cs.flexDirection.startsWith('column'))); };
                let chrome = px(getComputedStyle(el).marginBottom);
                for (let child = el, a = el.parentElement; a && a !== document.body; child = a, a = a.parentElement) {
                    if (stacked(a)) for (let s = child.nextElementSibling; s; s = s.nextElementSibling) chrome += outer(s);
                    const cs = getComputedStyle(a);
                    chrome += px(cs.paddingBottom) + px(cs.borderBottomWidth);
                    if (a === main) break;
                }
                const tailEl = tailSelector ? el.closest(tailSelector) : null;
                const tail = tailEl ? Math.max(0, tailEl.getBoundingClientRect().bottom - rect.bottom) : 0;
                const next = Math.max(min, Math.floor(window.innerHeight - top - chrome - tail));
                setHeight((prev) => (prev === next ? prev : next));
            });
        };
        measure();
        window.addEventListener('resize', measure);
        const main = document.getElementById('main');
        const ro = typeof ResizeObserver !== 'undefined' && main ? new ResizeObserver(measure) : null;
        if (ro) ro.observe(main);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', measure);
            if (ro) ro.disconnect();
        };
    }, [enabled, min, tailSelector, ref, node]);

    return { ref, height: active ? height : null, active };
}

/** The element a ref points at, as state: re-renders when it appears, changes or goes away. */
function useAttachedNode(ref) {
    const [node, setNode] = useState(null);
    // runs after every render on purpose; it only sets state when the element really changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
        if (ref.current !== node) setNode(ref.current);
    });
    return node;
}

/** Page tables given a viewport-relative limit ("70vh") fill the screen instead. */
export const isViewportLimit = (maxHeight) => typeof maxHeight === 'string' && /vh$/.test(maxHeight.trim());

/**
 * Scrolling + sticky header for a table box (DataTable scroll area, TableContainer):
 * - maxHeight given in px (a preview) or the table is in a dialog: used as given.
 * - maxHeight "70vh": fills the rest of the screen (useViewportFit).
 * - no maxHeight and the table fits the width: the PAGE scrolls and the header sticks under the
 *   top bar (pageSticky; the box drops its own overflow so the header can stick to the page).
 * - no maxHeight and the table is wider than the box: it needs its own horizontal scroll, so it
 *   fills the rest of the screen and the header sticks inside it.
 * Returns { ref, pageSticky, style }.
 */
export function useTableScroll({ maxHeight, tailSelector, min } = {}) {
    const ref = useRef(null);
    const [wide, setWide] = useState(false);
    const [inDialog, setInDialog] = useState(false);
    // What surrounds the table inside the page:
    //  'clip'     a panel with overflow hidden (fixed-height panel): the table keeps its own scroll
    //  'scroller' a panel that scrolls on its own: the header sticks to the top of that panel
    //  'page'     nothing: the page scrolls and the header sticks under the top bar
    const [surround, setSurround] = useState('page');
    const vh = isViewportLimit(maxHeight);
    const auto = maxHeight === undefined || maxHeight === null || maxHeight === '';
    const node = useAttachedNode(ref);

    useLayoutEffect(() => {
        const el = node;
        if (!el) return undefined;
        setInDialog(!!el.closest('[role="dialog"]'));
        if (!auto) return undefined;
        const surroundOf = () => {
            const main = document.getElementById('main');
            for (let a = el.parentElement; a && a !== main && a !== document.body; a = a.parentElement) {
                const oy = getComputedStyle(a).overflowY;
                if (oy === 'hidden' || oy === 'clip') return 'clip';
                if ((oy === 'auto' || oy === 'scroll') && a.scrollHeight > a.clientHeight + 1) return 'scroller';
            }
            return 'page';
        };
        const measure = () => {
            const table = el.querySelector('table');
            if (!table || el.offsetParent === null) return;
            const next = table.offsetWidth > el.clientWidth + 1;
            setWide((prev) => (prev === next ? prev : next));
            const around = surroundOf();
            setSurround((prev) => (prev === around ? prev : around));
        };
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        const table = el.querySelector('table');
        if (table) ro.observe(table);
        return () => ro.disconnect();
    }, [auto, node]);

    const clipped = surround === 'clip';
    const fit = useViewportFit(!inDialog && (vh || (auto && wide && !clipped)), { ref, tailSelector, min });
    const limit = fit.height ?? (auto ? undefined : maxHeight);
    const pageSticky = !inDialog && auto && !wide && !clipped;
    const style = { ...(limit ? { maxHeight: limit } : {}), ...(pageSticky && surround === 'scroller' ? { '--sticky-top': '0px' } : {}) };
    return { ref, pageSticky, style: Object.keys(style).length ? style : undefined };
}
